
import { useState, useEffect } from "react";
import { Movie, MoviesByDirector } from "@/types/movie";
import { organizeMoviesByDirector } from "@/context/movie/movieUtils";
import { useMultipleDirectorFilmographies } from "@/services/movie/directorFilmographyService";

/**
 * PHASE 4: Enhanced core hook with proper error handling for incomplete metadata
 * All movies now have complete metadata from universal enrichment service
 */
export const useMovieStateCore = (allMovies: Movie[]) => {
  const [favoriteMovies, setFavoriteMovies] = useState<MoviesByDirector>({});
  const [unseenDirectorMovies, setUnseenDirectorMovies] = useState<MoviesByDirector>({});
  const [planToWatchMovies, setPlanToWatchMovies] = useState<MoviesByDirector>({});
  const [combinedMovies, setCombinedMovies] = useState<Movie[]>([]);

  // Get favorite directors to fetch their filmographies
  const favorites = allMovies.filter(movie => movie.favorite);
  const favoriteDirectors = Object.keys(organizeMoviesByDirector(favorites));
  
  // Fetch filmographies for favorite directors - ALL movies have complete metadata from universal service
  const { 
    filmographyMovies, 
    isLoading: isFilmographyLoading,
    clearDirectorFilmography 
  } = useMultipleDirectorFilmographies(favoriteDirectors);

  // PHASE 4: Enhanced grouping with metadata validation and error handling
  useEffect(() => {
    // Favorites (Gems) - only user-added favorites
    const favoritesGrouped = organizeMoviesByDirector(favorites);
    setFavoriteMovies(favoritesGrouped);

    // User flag synchronization
    const userMovieStates = new Map<number, { favorite: boolean; planToWatch: boolean }>();
    allMovies.forEach(movie => {
      if (movie.tmdbId) {
        userMovieStates.set(movie.tmdbId, {
          favorite: movie.favorite,
          planToWatch: movie.planToWatch
        });
      }
    });

    // PHASE 4: Enhanced filmography processing with metadata validation
    let validFilmographyMovies: Movie[] = [];
    let skippedIncompleteMovies = 0;
    
    filmographyMovies.forEach(filmMovie => {
      // PHASE 4: Validate that movies have proper metadata
      if (!filmMovie.tags || filmMovie.tags.length === 0) {
        console.warn(`🎬 [MovieStateCore] PHASE 4: Skipping incomplete movie: ${filmMovie.title} - no genre data`);
        skippedIncompleteMovies++;
        return;
      }
      
      const userState = userMovieStates.get(filmMovie.tmdbId);
      if (userState) {
        // Sync user flags while preserving universal complete metadata
        validFilmographyMovies.push({
          ...filmMovie,
          favorite: userState.favorite,
          planToWatch: userState.planToWatch
        });
      } else {
        // All filmography movies already have complete metadata from universal service
        validFilmographyMovies.push({
          ...filmMovie,
          favorite: false,
          planToWatch: false
        });
      }
    });

    // PHASE 4: Log metadata quality metrics
    if (skippedIncompleteMovies > 0) {
      console.warn(`🎬 [MovieStateCore] PHASE 4: Skipped ${skippedIncompleteMovies} movies due to incomplete metadata`);
    }

    // Combined movies list with only complete metadata
    const userMovieIds = new Set(allMovies.map(m => m.tmdbId).filter(Boolean));
    const newCombinedMovies = [
      ...allMovies,
      // Only add filmography movies with complete metadata not already in user collection
      ...validFilmographyMovies.filter(filmMovie => 
        !userMovieIds.has(filmMovie.tmdbId)
      )
    ];

    setCombinedMovies(newCombinedMovies);

    // PHASE 4: Filter unseen gems with metadata validation
    const unseenByFavoriteDirectors = newCombinedMovies.filter(movie => {
      const isByFavoriteDirector = favoriteDirectors.includes(movie.director);
      const hasCompleteMetadata = movie.tags && movie.tags.length > 0;
      
      if (isByFavoriteDirector && !movie.favorite && !hasCompleteMetadata) {
        console.warn(`🎬 [MovieStateCore] PHASE 4: Excluding ${movie.title} from unseen gems - incomplete metadata`);
        return false;
      }
      
      return isByFavoriteDirector && !movie.favorite;
    });
    
    // FIXED: Apply proper year+popularity sorting and limit to top 5 per director
    const unseenGroupedRaw = organizeMoviesByDirector(unseenByFavoriteDirectors);
    const unseenGroupedLimited: MoviesByDirector = {};
    
    Object.keys(unseenGroupedRaw).forEach(director => {
      const directorMovies = unseenGroupedRaw[director];
      
      // Movies are already sorted by year (desc) then popularity (desc) from organizeMoviesByDirector
      // Just limit to top 5 without re-sorting to preserve the correct order
      unseenGroupedLimited[director] = directorMovies.slice(0, 5);
    });
    
    setUnseenDirectorMovies(unseenGroupedLimited);

    // Plan to watch (Watchlist) - from all combined movies
    const planToWatch = newCombinedMovies.filter(movie => movie.planToWatch);
    setPlanToWatchMovies(organizeMoviesByDirector(planToWatch));

    console.log(`🎬 [MovieStateCore] PHASE 4: Enhanced state updated:`, {
      favoriteDirectors: favoriteDirectors.length,
      unseenDirectors: Object.keys(unseenGroupedLimited).length,
      unseenMoviesTotal: Object.values(unseenGroupedLimited).reduce((sum, movies) => sum + movies.length, 0),
      validFilmographyMovies: validFilmographyMovies.length,
      skippedIncompleteMovies,
      combinedMovies: newCombinedMovies.length,
      planToWatchTotal: planToWatch.length,
      metadataQuality: "All movies validated for complete metadata",
      sortingMethod: "Year (desc) then popularity (desc) for same year - preserved order"
    });
  }, [allMovies, filmographyMovies, favoriteDirectors.join(',')]);

  // Clean up filmography cache when directors are removed from favorites
  useEffect(() => {
    const previousDirectors = new Set(Object.keys(favoriteMovies));
    const currentDirectors = new Set(favoriteDirectors);
    
    // Find directors that were removed from favorites
    const removedDirectors = [...previousDirectors].filter(director => 
      !currentDirectors.has(director)
    );

    // Clear filmography cache for removed directors
    removedDirectors.forEach(director => {
      console.log(`🎬 [MovieStateCore] Clearing filmography cache for removed director: ${director}`);
      clearDirectorFilmography(director);
    });
  }, [favoriteDirectors.join(','), clearDirectorFilmography]);

  return {
    favoriteMovies,
    unseenDirectorMovies,
    planToWatchMovies,
    combinedMovies,
    isFilmographyLoading
  };
};
